import { useState, useEffect, useCallback } from "react";
import Chart from "react-apexcharts";
import { DateRangePicker, Placeholder, SelectPicker } from "rsuite";
import { getEmailStats } from "../../services/emailAnalytics.services";

/** Format a Date as YYYY-MM-DD in local time */
const toYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const today = new Date();
const sevenDaysAgo = new Date(today);
sevenDaysAgo.setDate(today.getDate() - 6);

const PRESET_OPTIONS = [
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "Last 90 days", value: "90d" },
  { label: "Custom range", value: "custom" },
];

const AGGREGATION_OPTIONS = [
  { label: "Day", value: "day" },
  { label: "Week", value: "week" },
  { label: "Month", value: "month" },
];

/** A single summary metric card */
const StatCard = ({ label, value, rate, rateColor, subLabel }) => (
  <div className="col-sm-6 col-md-4 col-xl-2 mb-3">
    <div className="card custom-card h-100">
      <div className="card-body text-center p-3">
        <p className="tx-11 text-muted text-uppercase mb-1 font-weight-bold">
          {label}
        </p>
        {rate !== undefined ? (
          <>
            <h4 className="font-weight-bold mb-0" style={{ color: rateColor || "#495057" }}>
              {rate}%
            </h4>
            <p className="tx-12 text-muted mb-0">{value.toLocaleString()}</p>
          </>
        ) : (
          <h4 className="font-weight-bold mb-0">{value.toLocaleString()}</h4>
        )}
        {subLabel && <p className="tx-10 text-muted mb-0 mt-1">{subLabel}</p>}
      </div>
    </div>
  </div>
);

const EmailAnalytics = () => {
  const [preset, setPreset] = useState("7d");
  const [customRange, setCustomRange] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [aggregatedBy, setAggregatedBy] = useState("day");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  const [chartData, setChartData] = useState({
    series: [],
    options: {
      chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { curve: "smooth", width: 2 },
      markers: { size: 3 },
      xaxis: { categories: [], labels: { rotate: -30, style: { fontSize: "11px" } } },
      yaxis: { labels: { formatter: (val) => Math.floor(val) } },
      legend: { position: "top" },
      tooltip: { shared: true, intersect: false },
      colors: ["#5b73e8", "#1aad8d", "#f4a82e", "#2196f3", "#f46a6a", "#fd7e14", "#6f42c1"],
      grid: { borderColor: "#e9e9ef" },
    },
  });

  /** Resolve start / end dates from the current preset or custom range */
  const resolveDates = useCallback(() => {
    if (preset === "custom") {
      if (!customRange) return null;
      return { start: toYMD(customRange[0]), end: toYMD(customRange[1]) };
    }
    const end = new Date();
    const start = new Date();
    if (preset === "7d") start.setDate(end.getDate() - 6);
    else if (preset === "30d") start.setDate(end.getDate() - 29);
    else if (preset === "90d") start.setDate(end.getDate() - 89);
    return { start: toYMD(start), end: toYMD(end) };
  }, [preset, customRange]);

  const fetchData = useCallback(async () => {
    const dates = resolveDates();
    if (!dates) return;

    setLoading(true);
    setError(null);

    try {
      const res = await getEmailStats(dates.start, dates.end, aggregatedBy);
      const data = res?.data;
      setSummary(data?.summary || null);

      const chart = data?.chart || {};
      const labels = chart.labels || [];
      const s = chart.series || {};

      setChartData((prev) => ({
        ...prev,
        series: [
          { name: "Requests", data: s.requests || [] },
          { name: "Delivered", data: s.delivered || [] },
          { name: "Opens", data: s.opens || [] },
          { name: "Clicks", data: s.clicks || [] },
          { name: "Bounces", data: s.bounces || [] },
          { name: "Spam Reports", data: s.spam_reports || [] },
          { name: "Unsubscribes", data: s.unsubscribes || [] },
        ],
        options: {
          ...prev.options,
          xaxis: {
            ...prev.options.xaxis,
            categories: labels,
          },
        },
      }));
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load email analytics. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [resolveDates, aggregatedBy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePresetChange = (value) => {
    setPreset(value);
    setShowDatePicker(value === "custom");
    if (value !== "custom") setCustomRange(null);
  };

  return (
    <div className="row row-sm animation_fade">
      {/* Page header */}
      <div className="col-12 mb-3">
        <div className="page-header">
          <div>
            <h2 className="main-content-title tx-24 mg-b-5">Email Analytics</h2>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/admin/dashboard">Dashboard</a>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Email Analytics
              </li>
            </ol>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="col-12 mb-3">
        <div className="card custom-card">
          <div className="card-body d-flex flex-wrap align-items-center gap-3" style={{ gap: "12px" }}>
            <div>
              <label className="tx-12 text-muted d-block mb-1">Date Range</label>
              <SelectPicker
                value={preset}
                cleanable={false}
                onSelect={handlePresetChange}
                size="sm"
                data={PRESET_OPTIONS}
                style={{ width: 160 }}
                searchable={false}
              />
            </div>

            {showDatePicker && (
              <div>
                <label className="tx-12 text-muted d-block mb-1">Custom Range</label>
                <DateRangePicker
                  editable={false}
                  size="sm"
                  placeholder="Select dates"
                  placement="bottomStart"
                  disabledDate={(date) => date > new Date()}
                  onChange={(val) => setCustomRange(val && val.length === 2 ? val : null)}
                />
              </div>
            )}

            <div>
              <label className="tx-12 text-muted d-block mb-1">Aggregated By</label>
              <SelectPicker
                value={aggregatedBy}
                cleanable={false}
                onSelect={setAggregatedBy}
                size="sm"
                data={AGGREGATION_OPTIONS}
                style={{ width: 120 }}
                searchable={false}
              />
            </div>

            <div className="ml-auto" style={{ marginTop: "18px" }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={fetchData}
                disabled={loading}
              >
                {loading ? (
                  <span>
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    ></span>{" "}
                    Loading...
                  </span>
                ) : (
                  <span>
                    <i className="fa fa-refresh me-1"></i> Refresh
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="col-12 mb-3">
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="fa fa-exclamation-triangle me-2"></i>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Summary stat cards */}
      {loading && !summary ? (
        <div className="col-12 mb-3">
          <Placeholder.Graph active height={90} />
        </div>
      ) : summary ? (
        <div className="col-12 mb-3">
          <div className="row">
            <StatCard label="Requests" value={summary.requests} />
            <StatCard
              label="Delivered"
              value={summary.delivered.count}
              rate={summary.delivered.rate}
              rateColor="#1aad8d"
            />
            <StatCard
              label="Opened"
              value={summary.opens.count}
              rate={summary.opens.rate}
              rateColor="#2196f3"
            />
            <StatCard
              label="Clicked"
              value={summary.clicks.count}
              rate={summary.clicks.rate}
              rateColor="#5b73e8"
            />
            <StatCard
              label="Bounces"
              value={summary.bounces.count}
              rate={summary.bounces.rate}
              rateColor="#f46a6a"
            />
            <StatCard
              label="Spam Reports"
              value={summary.spam_reports.count}
              rate={summary.spam_reports.rate}
              rateColor="#fd7e14"
            />
          </div>

          {/* Secondary stats row */}
          <div className="row">
            <StatCard
              label="Unsubscribes"
              value={summary.unsubscribes.count}
              rate={summary.unsubscribes.rate}
              rateColor="#6f42c1"
            />
            <StatCard label="Blocks" value={summary.blocks} />
            <StatCard label="Deferred" value={summary.deferred} />
          </div>
        </div>
      ) : null}

      {/* Line chart */}
      <div className="col-12">
        <div className="card custom-card mg-b-20">
          <div className="card-body">
            <div className="card-header border-bottom-0 pt-0 pl-0 pr-0 d-flex align-items-center">
              <label className="main-content-label mb-0">
                Email Activity Over Time
              </label>
              {summary && (
                <span className="tx-12 text-muted ms-2">
                  ({resolveDates()?.start} – {resolveDates()?.end})
                </span>
              )}
            </div>

            {loading ? (
              <Placeholder.Graph active height={350} />
            ) : (
              <Chart
                options={chartData.options}
                series={chartData.series}
                type="line"
                height={350}
              />
            )}
          </div>
        </div>
      </div>

      {/* Info note */}
      <div className="col-12">
        <p className="tx-12 text-muted text-center mb-4">
          Data is pulled in real-time from the SendGrid Stats API. Rates are
          calculated as a percentage of requests (for bounces/spam) or delivered
          emails (for opens/clicks/unsubscribes).
        </p>
      </div>
    </div>
  );
};

export default EmailAnalytics;
